import { useEffect, useRef } from 'react';
import { useMRE, DEFAULT_CAMERA_MARGINS } from "@strava/feature-global-wasm-mre";
import type { CameraTarget } from "@strava/mre";

interface MapResizeHandlerProps {
  grabberElement?: HTMLElement | null;
}

/**
 * Adjusts the MRE camera after a resize so that the map reveals more geographic area.
 *
 * MRE uses Ogre's constant-vertical-FOV projection. When the viewport gets
 * taller, the vertical field of view stays fixed — so the visible geographic
 * area doesn't change, it just renders at a higher pixel density. The legacy Mapbox
 * version instead kept meters-per-pixel constant, so a taller viewport revealed more
 * area at the same zoom. We want to keep the Mapbox behaviour, so we have to manually 
 * adjust the camera after the resize.
 *
 * To match Mapbox behaviour we:
 * 1. Freeze the MRE container height during drag so the canvas doesn't scale
 * 2. On drag complete, use the camera's own projection (getCustomLookAtPoint)
 *    to find where the new viewport center should be, keeping the top edge fixed
 * 3. Adjust camera distance proportionally to maintain meters-per-pixel
 */
export const MapResizeHandler = ({ grabberElement }: MapResizeHandlerProps) => {
  const { terrainEngine, mapContainerRef } = useMRE();
  const savedRef = useRef<{
    distance: number;
    height: number;
    target: CameraTarget;
  } | null>(null);

  useEffect(() => {
    if (!terrainEngine || !mapContainerRef.current || !grabberElement) {
      return undefined;
    }

    const camera = terrainEngine.getCamera();
    const mapContainer = mapContainerRef.current;

    const onDragStart = () => {
      const target = camera.getTarget();
      const height = mapContainer?.clientHeight;
      if (!height) return;

      savedRef.current = {
        distance: target.distance,
        height,
        target
      };

      // Freeze the MRE container at its current height so the canvas
      // doesn't resize (and re-render) during the drag. The space between
      // the frozen container and the growing #map-canvas shows as white.
      if (mapContainer) {
        mapContainer.style.height = `${height}px`;
      }
    };

    const onResizeMap = () => {
      const saved = savedRef.current;

      if (!saved) {
        if (mapContainer) {
          mapContainer.style.height = '';
        }
        return;
      }

      // Read the new height from #map-canvas (which the Resizer updated)
      // while the MRE container is still frozen at the old height.
      const mapCanvas = mapContainer?.closest('#map-canvas');
      const newHeight = mapCanvas?.clientHeight ?? 0;

      if (!newHeight || newHeight === saved.height) {
        if (mapContainer) {
          mapContainer.style.height = '';
        }
        return;
      }

      // While the canvas is still frozen at the old height, use the camera's
      // own projection to find the geographic point that should become the new
      // viewport center. This keeps the top edge fixed and reveals new area below.
      //
      // In the old viewport (screen Y: 0=top, 1=bottom), the point that will
      // become the new center is at screenY = newHeight / (2 * oldHeight).
      // For example: growing from 400px to 600px -> screenY = 0.75, meaning
      // the new center is 3/4 of the way down the old viewport.
      const newCenterScreenY = Math.min(newHeight / (2 * saved.height), 1.0);
      const newCenterPoint = camera.getCustomLookAtPoint(0.5, newCenterScreenY);

      // Release the frozen height so the MRE canvas expands to fill #map-canvas.
      if (mapContainer) {
        mapContainer.style.height = '';
      }

      const heightRatio = newHeight / saved.height;

      const adjustedTarget: CameraTarget = newCenterPoint.isPointingAtMap
        ? {
            lookAtPoint: {
              latitude: newCenterPoint.worldPoint.latitude,
              longitude: newCenterPoint.worldPoint.longitude,
              elevation: saved.target.lookAtPoint.elevation
            },
            bearing: saved.target.bearing,
            pitch: saved.target.pitch,
            distance: saved.distance * heightRatio
          }
        : {
            // Fallback: just adjust distance without shifting
            lookAtPoint: saved.target.lookAtPoint,
            bearing: saved.target.bearing,
            pitch: saved.target.pitch,
            distance: saved.distance * heightRatio
          };

      camera.flyToTarget(adjustedTarget, 0, false, DEFAULT_CAMERA_MARGINS, undefined);

      savedRef.current = {
        distance: adjustedTarget.distance,
        height: newHeight,
        target: adjustedTarget
      };
    };

    grabberElement.addEventListener('mousedown', onDragStart);
    document.addEventListener('ResizeMap', onResizeMap);

    // Cleanup function: remove listeners and reset height
    return () => {
      grabberElement.removeEventListener('mousedown', onDragStart);
      document.removeEventListener('ResizeMap', onResizeMap);
      if (mapContainer) {
        mapContainer.style.height = '';
      }
    };
  }, [terrainEngine, mapContainerRef, grabberElement]);

  return null;
};

export default MapResizeHandler;